import { runWithErrorReporting } from "$root/api/error-reporting";
import { IconWalls, WallOverlay } from "$root/components/IconWall";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { useEventBus } from "$root/event-bus";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import { buildUniverseInstrumentExportConfig } from "$root/functional-areas/instruments/export";
import { createInstrumentList, instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { downloadContentDisposition, objMatchFn, ToastableError } from "$root/utils";
import { Card } from "$root/widgets-architecture/layout/Card";
import type { ReferenceUniverseDetails } from "@eu.mdotm.api/sphere";
import { ActionText, AutoTooltip, Icon } from "@mdotm/shadcn/legacy/components";
import { TextInput, DropdownMenu } from "@mdotm/shadcn/mdotm/components";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";

export function UniverseCompositionCard({ universe }: { universe: ReferenceUniverseDetails }): React.JSX.Element {
	const universeUuid = universe.uuid;
	const universeName = universe.name;
	const { t } = useTranslation();
	const columns = columnsDefinition.useInstrumentUniverseComposition("read");
	const queryUniverseComposition = universeRepository.composition.useQuery(universeUuid!);
	const queryUserInstrumentsMap = useQuery({
		...instrumentsRepository.userClassifications.getQueryConfig(
			queryUniverseComposition.data?.composition?.map((entry) => entry.ticker).filter((ticker) => ticker != null) ?? [],
		),
		enabled: queryUniverseComposition.data != null,
	});

	const queryInvestmentsRichInfo = useQuery({
		...instrumentsRepository.investmentsRichInfo.getQueryConfig(queryUniverseComposition.data?.composition ?? []),
		enabled: queryUniverseComposition.data != null,
	});

	const queryComposition = useMappedQueries(
		([universeComposition, userInstrumentsMap, investmentsRichInfo]) => {
			const instruments = createInstrumentList({
				composition: universeComposition.composition ?? [],
				userInstrumentsMap,
				investmentData: investmentsRichInfo,
			});
			return { instruments };
		},
		[],
		[queryUniverseComposition, queryUserInstrumentsMap, queryInvestmentsRichInfo],
	);

	const [query, setQuery] = useState<string>("");
	const { filtered } = useSearchable({
		query: query,
		collection: queryComposition.data?.instruments ?? [],
		extractFn: (instrument) =>
			`${instrument.name}${instrument.identifiers?.ALIAS}${instrument.identifiers?.ISIN}${instrument.identifiers?.CUSIP}${instrument.identifiers?.BLOOMBERG_TICKER}`.toLowerCase(),
	});

	useEventBus("investment-update", {
		filter: objMatchFn({ uuid: universeUuid }),
		listener: () => {
			queryUniverseComposition.refetch().catch(noop);
		},
	});

	const persistColProps = agExtra.usePersistColState(persistedColStateKeys["tableV3-universe-composition-read"]);

	return (
		<Card
			title={t("UNIVERSE.READ_ONLY.NAME")}
			tooltip={t("UNIVERSE.READ_ONLY.DESCRIPTION")}
			actionHeader={
				<DropdownMenu
					Trigger={WidgetDownloadTrigger}
					actions={[
						{
							icon: "xls",
							onClickAsync: async () => {
								await runWithErrorReporting(
									async () => {
										if (!universeUuid) {
											throw new ToastableError("Something went wrong", { icon: "Portfolio" });
										}

										const config = buildUniverseInstrumentExportConfig(universeUuid);
										const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
										await downloadContentDisposition(file);
									},
									{
										area: "universe",
										attemptedOperation: { message: "try to download universe composition" },
									},
								);
							},
							label: "Instrument list",
						},
					]}
				/>
			}
		>
			<WallOverlay
				showOverlay={queryComposition.isLoading || queryComposition.isFetching || queryComposition.isError}
				overlay={queryComposition.isError ? <IconWalls.ErrorData /> : <IconWalls.InfiniteProgress />}
				className="min-h-0 grow flex flex-col"
				walledAppearance={{ className: "min-h-0 grow flex flex-col" }}
			>
				<div className="flex justify-between mb-4">
					<div className="max-w-[500px] flex-1">
						<TextInput
							leftContent={<Icon icon="Search" />}
							value={query}
							onChangeText={setQuery}
							placeholder="Filter by instrument name, ISIN, asset class or micro asset class"
						/>
					</div>
					<div className="flex space-x-1 items-end">
						<ActionText
							color={themeCSSVars.palette_N400}
							href={typedUrlForRoute("PortfolioStudioSettings/InstrumentCustomisation", {
								universeUuid,
								universeName,
							})}
						>
							Instruments customisation
						</ActionText>

						<AutoTooltip
							position="top"
							align="endToStart"
							mode="hover"
							overrideColor={themeCSSVars.palette_N400}
							trigger={({ innerRef }) => (
								<div ref={innerRef}>
									<Icon icon="info" color={themeCSSVars.palette_N400} size={18} />
								</div>
							)}
						>
							Explore all instruments used within your account, generate descriptions, and customize instrument details.
						</AutoTooltip>
					</div>
				</div>
				<ReactQueryWrapperBase query={queryComposition} keepChildren>
					{() => (
						<AgGridWithModules<Instrument>
							{...persistColProps}
							columnDefs={columns}
							rowData={filtered}
							getRowId={(x) => x.data.tickerName!}
						/>
					)}
				</ReactQueryWrapperBase>
			</WallOverlay>
		</Card>
	);
}
